<template>
  <div class="col-sm-12">
    <div class="card">
      <!-- Single row for all controls -->
      <div class="dropdown-item page-item">
        <div class="page-grid">
          <!-- For non-distributor users: show dropdown, search bar, and create button -->
          <template v-if="!isDistributorUser">
            <!-- Distributor Dropdown -->
            <div class="page-search-container dropdown-item">
              <div style="display: flex; align-items: center; gap: 10px;">
                <label for="distributor" style="font-weight:700; white-space: nowrap;">Distributor : </label>
                <select
                    id="title"
                    v-model="selectedDistributor"
                    class="scrollable-dropdown select-distributor"
                    @change="onDistributorChange"
                >
                  <option disabled value="">Select Distributor</option>
                  <option v-for="distributor in distributorList" :key="distributor.profile_id" :value="distributor.profile_id">
                    {{ distributor.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Search Bar -->
            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealers by name, mobile"
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>

            <!-- Create Button -->
            <button type="button" class="btn button-default new-item-btn" @click="addNewDealer">
              Add New Dealer
            </button>
          </template>

          <!-- For distributor users: show only search bar and create button -->
          <template v-else>
            <!-- Search Bar -->
            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealers by name, mobile"
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>

            <!-- Create Button -->
            <button type="button" class="btn button-default new-item-btn" @click="addNewDealer">
              Add New Dealer
            </button>
          </template>
        </div>
      </div>

      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <div class="table-responsive" v-if="dealerList.length > 0">
        <table class="table">
          <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Name</th>
            <th scope="col">Category</th>
            <th scope="col">Mobile Number</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="dealer in filteredDealers" :key="dealer.id">
            <td>{{ dealer.name}}</td>
            <td>{{ dealer.dealer_cat_name}}</td>
            <td>{{ dealer.mobile }}</td>
            <td>
                <span :class="{
                  'status-label status-yes': dealer.is_active === '1',
                  'status-label status-no': dealer.is_active === '0'
                }">
                  {{ dealer.is_active === '1' ? 'Active' : 'Inactive' }}
                </span>
            </td>
            <td>
              <i class="fa fa-edit" @click="editDetails(dealer.id)"></i>
              <i class="fa fa-eye action-icon" aria-hidden="true" @click="viewDetails(dealer.id)"></i>
              <i :class="dealer.is_verified == 1 
                  ? 'fa fa-check-circle action-icon text-success' 
                  : 'fa fa-shield action-icon'"
                :title="dealer.is_verified == 1 ? 'Dealer Verified' : 'Verify Dealer'"
                @click="dealer.is_verified == 0 && verifyDealer(dealer.id)">
              </i>
            </td>
          </tr>
          <tr v-if="filteredDealers.length === 0">
            <td colspan="5" class="text-center">No dealers found matching your search</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="!loading && selectedDistributor" class="no-data-message">
        No dealers found for the selected distributor.
      </div>

      <div v-else-if="!loading && isDistributorUser && dealerList.length === 0" class="no-data-message">
        No dealers found for your account.
      </div>
    </div>
  </div>

  <!-- Modal Component -->
  <ViewDealerInfoModal
      :show="showModal"
      :dealerId="selectedDealerId"
      @close="closeModal"
  />
</template>

<script>
import { useRouter } from "vue-router";
import {
  fetchDealersByDistributors,
  fetchDistributorsList
} from "@/services/dataService";
import ViewDealerInfoModal from "@/pages/Modal/viewDealerInfo.vue";

export default {
  name: 'DealerList',
  components: {
    ViewDealerInfoModal
  },
  data() {
    return {
      loading: false,
      token: localStorage.getItem("token_set") || "",
      profile_id: localStorage.getItem("profile_id"),
      dealerList: [],
      distributorList: [],
      selectedDistributor: "",
      searchFilter: "",
      showModal: false,
      selectedDealerId: null,
      isDistributorUser: false
    }
  },
  watch: {
    searchFilter(newVal) {
      // Search filter watcher - functionality moved to computed property
    },
    selectedDistributor(newVal) {
      // Removed automatic search call from here to prevent double calls
    },
    showModal(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }
  },
  computed: {
    // perform search
    filteredDealers() {
      return [...this.dealerList]
          .filter(dealer =>
              (dealer.name || '').toLowerCase().includes(this.searchFilter.toLowerCase()) ||
              (dealer.mobile || '').includes(this.searchFilter)
          );
    }
  },
  methods: {
    // fetch distributor list
    async fetchDistributors() {
      try {
        const response = await fetchDistributorsList(this.profile_id, this.token);
        this.distributorList = response.data.data || [];
        console.log('Fetched Distributors:', this.distributorList);
      } catch (error) {
        console.error('Error fetching Distributors:', error);
      }
    },

    async getDealers() {
      try {
        this.loading = true;
        this.dealerList = []; // Reset dealer list

        if (!this.selectedDistributor) {
          console.warn("No distributor selected.");
          return;
        }
        const response = await fetchDealersByDistributors(this.selectedDistributor, this.token);
        this.dealerList = response.data?.data || [];
        console.log('Fetched Dealers:', this.dealerList);
      } catch (error) {
        console.error("Error fetching dealer list:", error);
        this.dealerList = [];
      } finally {
        this.loading = false;
      }
    },

    // Called when distributor selection changes
    onDistributorChange() {
      if (this.selectedDistributor) {
        this.getDealers();
      }
    },

    addNewDealer() {
      this.$router.push({ path: '/add-dealer' });
    },

    // go to edit dealer details page
    editDetails(id) {
      this.$router.push({ path: `/edit-dealer/${id}` });
    },

    // show modal with dealer details
    viewDetails(id) {
      this.selectedDealerId = id;
      this.showModal = true;
    },

    // go to verify dealer page
    verifyDealer(id) {
      this.$router.push({ path: `/verify-dealer/${id}` });
    },

    // close modal
    closeModal() {
      this.showModal = false;
      this.selectedDealerId = null;
    },

    // Initialize user type and auto-load data for distributors
    initializeUserSettings() {
      const userLabel = localStorage.getItem("user_label");
      this.isDistributorUser = userLabel === "DISTRIBUTOR" || userLabel === "DISTRIBUTOR BACK OFFICE";

      if (this.isDistributorUser) {
        // For distributor users, automatically set selected distributor and load dealers
        this.selectedDistributor = userLabel === "DISTRIBUTOR BACK OFFICE" ? localStorage.getItem("reportingL1ProfileId") : this.profile_id;
        this.getDealers();
      }
    }
  },
  setup() {
    const router = useRouter();

    return {
      router
    };
  },
  mounted() {
    if (!this.isDistributorUser) {
      this.fetchDistributors();
    }
    this.initializeUserSettings();
  }
};
</script>

<style scoped lang="scss">
/* Apply the same styling pattern as item screen */
.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.page-search-container.dropdown-item {
  flex-grow: 0;
  flex-basis: auto;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
  min-width: 250px;
}

.search-input:hover {
  border-color: #0056b3;
}

.new-item-btn {
  margin-left: auto;
  padding: 10px 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.select-distributor {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  min-width: 250px;
}

/* Keep the rest of your existing dealer screen styles */
.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-shield {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

i:hover {
  color: #0056b3;
  transform: scale(1.2);
}

i:active {
  transform: scale(1.1);
}

.action-icon {
  margin-right: 10px;
}

.status-label {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 5px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 90px;
  max-width: 90px;
  text-align: center;
}

.status-yes {
  background-color: #069E27;
  color: #fff;
  border-radius: 4px;
}

.status-no {
  background-color: red;
  color: #fff;
  border-radius: 4px;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.text-center {
  text-align: center;
}

.scrollable-dropdown {
  max-height: 200px;
  overflow-y: auto;
}

/* Focus states */
input:focus, select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.button-default {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.button-default:hover {
  color: #fff;
  background-color: #0056b3;
  border-color: #004085;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .page-search-container {
    width: 100%;
  }

  .search-input, .select-distributor {
    min-width: 100%;
  }

  .new-item-btn {
    margin-left: 0;
    width: 100%;
  }
}
</style>